import { attributionKeys, setAdvertisingConsent } from './attribution'

export type CookieConsent = { analytics: boolean; advertising: boolean }
const CONSENT_KEY = 'canhoto:consent:v1'
const ga4Id = (import.meta.env.VITE_GA4_ID ?? '').trim()
const adsId = (import.meta.env.VITE_GADS_ID ?? '').trim()
const signupLabel = (import.meta.env.VITE_GADS_SIGNUP_LABEL ?? '').trim()
export const measurementEnabled = Boolean(ga4Id || adsId)
const denied: CookieConsent = { analytics: false, advertising: false }
let initialized = false
let lastPage: string | null = null
const recordedAccounts = new Set<string>()

// Match gtag's queue format (Arguments objects, not event objects).
declare global {
  interface Window {
    [key: `ga-disable-${string}`]: boolean
    dataLayer?: IArguments[]
    gtag?: (...args: unknown[]) => void
  }
}
function gtag(...args: unknown[]) { window.gtag?.(...args) }

export function readConsent(): CookieConsent | null {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null')
    if (value && typeof value === 'object' && 'analytics' in value && 'advertising' in value
      && typeof value.analytics === 'boolean' && typeof value.advertising === 'boolean') return { analytics: value.analytics, advertising: value.advertising }
  } catch { /* A missing or invalid preference means denied. */ }
  return null
}

function consentSignals(consent: CookieConsent) {
  return {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.advertising ? 'granted' : 'denied',
    ad_user_data: consent.advertising ? 'granted' : 'denied',
    ad_personalization: consent.advertising ? 'granted' : 'denied',
  }
}

export function initializeMeasurement() {
  if (initialized || !measurementEnabled || typeof window === 'undefined') return
  initialized = true
  window.dataLayer ??= []
  // Google consumes the standard gtag Arguments queue.
  // eslint-disable-next-line prefer-rest-params
  window.gtag ??= function () { window.dataLayer!.push(arguments) }
  gtag('consent', 'default', { ...consentSignals(denied), wait_for_update: 500 })
  gtag('set', 'url_passthrough', true)
  gtag('set', 'ads_data_redaction', true)
  const saved = readConsent()
  if (saved) gtag('consent', 'update', consentSignals(saved))
  gtag('js', new Date())
  // Manual SPA pageviews. Disable history pageviews in GA4 Enhanced Measurement.
  const config = { send_page_view: false, allow_google_signals: false, page_location: safePageUrl(window.location.pathname, window.location.search), page_referrer: safeReferrer() }
  if (ga4Id) gtag('config', ga4Id, config)
  if (adsId) gtag('config', adsId, { ...config, allow_enhanced_conversions: true })
  if (!document.getElementById('google-measurement')) {
    const script = document.createElement('script')
    script.id = 'google-measurement'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id || adsId)}`
    document.head.appendChild(script)
  }
}

export function saveConsent(consent: CookieConsent) {
  const previousConsent = activeConsent ?? readConsent()
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(consent)) } catch { /* Keep the in-page choice even when storage is unavailable. */ }
  activeConsent = consent
  setAdvertisingConsent(consent.advertising)
  if (initialized) {
    gtag('consent', 'update', consentSignals(consent))
    if (!consent.advertising) gtag('set', 'user_data', null)
    if (consent.analytics && !previousConsent?.analytics && typeof window !== 'undefined') {
      lastPage = null
      trackPage(window.location.pathname, window.location.search)
    }
  }
}
let activeConsent: CookieConsent | null = null

export function isPublicMeasurementPath(path: string) {
  return ['/', '/produto', '/planos', '/custom', '/proposta', '/contato', '/login', '/criar-conta', '/privacidade', '/termos'].includes(path.replace(/\/+$/, '') || '/')
}
function safePageUrl(path: string, search: string) {
  const query = new URLSearchParams(search)
  const safe = new URLSearchParams()
  for (const key of attributionKeys) { const value = query.get(key); if (value) safe.set(key, value) }
  return `${window.location.origin}${path}${safe.size ? `?${safe.toString()}` : ''}`
}
function safeReferrer() {
  try { const url = new URL(document.referrer); return `${url.origin}${url.pathname}` } catch { return '' }
}

export function trackPage(path: string, search: string) {
  if (ga4Id) window[`ga-disable-${ga4Id}`] = !isPublicMeasurementPath(path)
  if (!isPublicMeasurementPath(path)) {
    if (initialized) gtag('consent', 'update', consentSignals(denied))
    lastPage = null
    return
  }
  const wasInitialized = initialized
  initializeMeasurement()
  if (!initialized) return
  const page = safePageUrl(path, search)
  if (lastPage === page) return
  if (wasInitialized) gtag('consent', 'update', consentSignals(activeConsent ?? readConsent() ?? denied))
  const referrer = lastPage ?? safeReferrer()
  lastPage = page
  gtag('set', { page_location: page, page_referrer: referrer })
  if (ga4Id) gtag('event', 'page_view', { send_to: ga4Id, page_location: page, page_referrer: referrer })
}

function accountKey(result: unknown): string {
  if (result && typeof result === 'object' && 'account' in result && result.account && typeof result.account === 'object' && 'id' in result.account
    && (typeof result.account.id === 'string' || typeof result.account.id === 'number')) return String(result.account.id)
  return 'created-in-session'
}

export function trackSignup(result: unknown, plan: string, email: string) {
  initializeMeasurement()
  if (!initialized) return
  const key = `canhoto:signup:${accountKey(result)}`
  if (recordedAccounts.has(key)) return
  try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1') } catch { /* The in-memory guard still prevents duplicates. */ }
  recordedAccounts.add(key)
  if (ga4Id) gtag('event', 'sign_up', { send_to: ga4Id, method: 'trial', plan })
  if (adsId && signupLabel) {
    if ((activeConsent ?? readConsent())?.advertising) gtag('set', 'user_data', { email: email.trim().toLowerCase() })
    gtag('event', 'conversion', { send_to: `${adsId}/${signupLabel}` })
    gtag('set', 'user_data', null)
  }
}

export function trackLead(subject: string) {
  initializeMeasurement()
  if (initialized && ga4Id && ['demonstration', 'plans', 'quote'].includes(subject)) gtag('event', 'generate_lead', { send_to: ga4Id, subject })
}

export function trackWhatsAppClick(page: 'home' | 'plans' | 'contact' | 'product') {
  initializeMeasurement()
  if (!initialized) return
  const consent = activeConsent ?? readConsent() ?? denied
  if (consent.analytics && ga4Id) gtag('event', 'whatsapp_click', { send_to: ga4Id, page })
  if (consent.advertising && adsId) gtag('event', 'whatsapp_click', { send_to: adsId, page })
}
